import { Children, JSX, MouseEventHandler, ReactElement, Ref } from 'react';
import { APIScopeData, QueryBuilderQuery } from '@ardoq/api-types';
import {
  ButtonWithDropdown,
  CustomSelectBase,
  DropdownOption,
} from '@ardoq/dropdown-menu';
import { actionNamespaceSelectComponentHierarchy } from '@ardoq/global-consts';
import { Locale } from '@ardoq/locale';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { SelectComponentRoot } from '../SelectComponentRoot';
import {
  selectComponentSelectionChanged,
  setComponentSelectQuery,
} from '../../actions';
import {
  selectedComponentIdsOnOpeningDropdown,
  setFilterTerm,
} from '../../actions/navigatorActions';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { waitForComponentHierarchyToBeMounted } from './waitForComponentHierarchyToBeMounted';
import { isSameSet } from '@ardoq/common-helpers';
import { IconName } from '@ardoq/icons';
import { selectComponentsInDropdownWorkspaceOptions$ } from '../selectComponentHierarchyConfiguration';
import { getWorkspaceOptionsTooltip } from '../../utils/utils';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

export type ComponentSelectorProps = {
  componentQuery?: QueryBuilderQuery | null;
  /**
   * Callback fired when components are selected in the dropdown.
   * Use scopeData.scopeComponents to get the selected component IDs.
   * @param scopeData
   */
  onComponentsSelected: (scopeData: APIScopeData) => void;
  children?: ReactElement<{
    onClick?: MouseEventHandler;
    ref?: Ref<HTMLElement> | undefined;
    style?: React.CSSProperties;
  }>;
  minDropdownSize?: DropdownSize;
  selectedComponentIds?: string[];
  /**
   * When true, displays a search input at the top of the dropdown menu
   * and enables workspace filtering controls.
   */
  hasSearchInputInDropdown?: boolean;
  placeholderText?: string;
  onMenuOpen?: VoidFunction;
  locale: Locale;
  i18nLocale?: I18nLocale;
};

export const ComponentSelector = ({
  componentQuery,
  onComponentsSelected,
  children,
  minDropdownSize,
  selectedComponentIds,
  hasSearchInputInDropdown,
  placeholderText,
  onMenuOpen,
  locale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: ComponentSelectorProps): JSX.Element => {
  const resolvedPlaceholderText =
    placeholderText ??
    t(
      i18nLocale,
      'componentHierarchies:componentSelector.placeholder',
      'Search for components'
    );

  const child = children ? Children.only(children) : undefined;

  const handleMenuOpen = async () => {
    try {
      await waitForComponentHierarchyToBeMounted(
        actionNamespaceSelectComponentHierarchy
      );

      if (!componentQuery) {
        return;
      }

      dispatchAction(setComponentSelectQuery({ query: componentQuery }));

      if (selectedComponentIds) {
        dispatchAction(
          selectedComponentIdsOnOpeningDropdown(selectedComponentIds)
        );
      }
    } catch (e) {
      // Timeout waiting for component hierarchy to be mounted.
      // Ignore as the dropdown can still open without it.
    }
    onMenuOpen?.();
  };

  const onComponentsSelectedWrapped = (scopeData: APIScopeData) => {
    const newSelection = scopeData.scopeComponents;
    // If the user opens the dropdown, we sync the selection in the dropdown
    // with the selectedComponentIds. This will trigger the internal callback,
    // which should not be propagated to the consumers of the component.
    if (selectedComponentIds && isSameSet(newSelection, selectedComponentIds)) {
      return;
    }
    onComponentsSelected(scopeData);
  };

  return (
    <CustomSelectBase
      placeholderText={resolvedPlaceholderText}
      onMenuOpen={handleMenuOpen}
      onOptionsChanged={onComponentsSelectedWrapped}
      optionsChangedAction={selectComponentSelectionChanged}
      actionNamespace={actionNamespaceSelectComponentHierarchy}
      onFilterChanged={onFilterChanged}
      selectButton={child}
      minDropdownSize={minDropdownSize}
      hasSearchInputInDropdown={hasSearchInputInDropdown}
      additionalSearchControls={
        hasSearchInputInDropdown ? (
          <WorkspaceOptionsConnected i18nLocale={i18nLocale} />
        ) : undefined
      }
    >
      <SelectComponentRoot i18nLocale={i18nLocale} locale={locale} />
    </CustomSelectBase>
  );
};

const onFilterChanged = (filterTerm: string) => {
  dispatchAction(
    setFilterTerm(filterTerm),
    actionNamespaceSelectComponentHierarchy
  );
};

type WorkspaceOptionsProps = {
  options: DropdownOption[];
  i18nLocale: I18nLocale;
};

const WorkspaceOptions = ({ options, i18nLocale }: WorkspaceOptionsProps) => {
  const tooltipText = getWorkspaceOptionsTooltip(options, i18nLocale);
  return (
    <ButtonWithDropdown
      iconName={IconName.FILTER_LIST}
      tooltipText={tooltipText}
      aria-label={tooltipText}
      options={options}
      isKeepOpen={true}
      isDisabled={options.length <= 1}
    />
  );
};

const WorkspaceOptionsConnected = connect(
  WorkspaceOptions,
  selectComponentsInDropdownWorkspaceOptions$
);
